import React, { useEffect, useRef, useState } from 'react';
import { WEB_MODAL_MAX_WIDTH } from '../../theme/webLayoutWidths';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import { Button, Dialog, IconButton, Portal, Text } from 'react-native-paper';
import Animated, {
  Layout,
  ZoomIn,
  ZoomOut,
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { Platform } from 'react-native';
import ReviewService from '../../services/ReviewService';
import { appAnalytics } from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import type { ReviewSource } from '../../services/appAnalyticsTypes';
import { GTFonts, useTheme } from '../../theme';
import { fs, iconSize, v, s } from '../../utils/libs';
import Typography from './Typography';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';

type PendingRequest = {
  resolve: (
    v: boolean | { accepted: boolean; rating?: number; openedStore?: boolean },
  ) => void;
  title?: string;
  message?: string;
  defaultRating?: number;
  hideStars?: boolean;
  source: ReviewSource;
};

const MAX_STARS = 5;

const RatingPromptHost: React.FC = () => {
  const { theme } = useTheme();
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);
  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState('Loving the App?');
  const [message, setMessage] = useState(
    `Your support means everything! Could you spare a moment to give us a quick ${
      Platform.OS === 'ios' ? 'Apple App Store' : 'Google Play Store'
    } review?\n\nYour positive review helps other people find the app. 💙`,
  );
  const [rating, setRating] = useState<number>(5);
  const [hideStars, setHideStars] = useState(false);
  const pendingRef = useRef<PendingRequest | null>(null);

  useEffect(() => {
    // Register presenter once
    ReviewService.registerFallbackPromptPresenter(async params => {
      return new Promise<
        boolean | { accepted: boolean; rating?: number; openedStore?: boolean }
      >(resolve => {
        pendingRef.current = {
          resolve,
          title: params.title,
          message: params.message,
          defaultRating: params.defaultRating,
          hideStars: params.hideStars,
          source: params.source,
        };
        setTitle(params.title || 'Loving the App?');
        setMessage(
          params.message ||
            `Your support means everything! Could you spare a moment to give us a quick ${
              Platform.OS === 'ios' ? 'Apple App Store' : 'Google Play Store'
            } review?\n\nYour positive review helps other people find the app. 💙`,
        );
        setRating(params.defaultRating ?? 5);
        setHideStars(!!params.hideStars);
        setVisible(true);
        appAnalytics.track('review_modal_shown', {
          ...buildAppAnalyticsContext(),
          source: params.source,
        });
      });
    });
  }, []);

  const onAccept = () => {
    const p = pendingRef.current;
    const source = p?.source ?? 'sidebar';
    setVisible(false);
    pendingRef.current = null;

    appAnalytics.track('review_store_opened', {
      ...buildAppAnalyticsContext(),
      source,
      channel: ReviewService.getStoreChannel(),
    });

    // On web, open the Play Store in a NEW tab synchronously (avoids popup blockers).
    if (Platform.OS === 'web') {
      const url = ReviewService.getGooglePlayListingUrl();
      if (url) {
        try {
          const doc: any = (globalThis as any)?.document;
          const win: any = (globalThis as any)?.window;
          if (doc?.createElement) {
            const a = doc.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.click();
          } else if (win?.open) {
            win.open(url, '_blank', 'noopener,noreferrer');
          }
        } catch { }
        p?.resolve({ accepted: true, rating, openedStore: true });
        return;
      }
    }
    p?.resolve(true);
  };
  const onCancel = (reason: 'later' | 'backdrop' = 'later') => {
    const p = pendingRef.current;
    const source = p?.source ?? 'sidebar';
    setVisible(false);
    pendingRef.current = null;
    appAnalytics.track('review_modal_dismissed', {
      ...buildAppAnalyticsContext(),
      source,
      reason,
    });
    p?.resolve(false);
  };

  return (
    <Portal>
      <Dialog
        visible={visible}
        onDismiss={() => onCancel('backdrop')}
        style={[
          styles.dialog,
          constrainWidth ? styles.dialogConstrained : null,
          {
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.md,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: theme.colors.borderGray,
          },
        ]}>
        <Animated.View entering={ZoomIn} exiting={ZoomOut} layout={Layout}>
          <Dialog.Title style={styles.title}>{title}</Dialog.Title>
          <Dialog.Content>
            <Typography style={styles.message}>{message}</Typography>
            {!hideStars ? (
              <View style={styles.starsRow}>
                {Array.from({ length: MAX_STARS }).map((_, idx) => {
                  const starIndex = idx + 1;
                  const filled = starIndex <= rating;
                  return (
                    <Star
                      key={starIndex}
                      index={starIndex}
                      filled={filled}
                      // onPress={() => setRating(starIndex)}
                      onPress={() => { }}
                    />
                  );
                })}
              </View>
            ) : null}
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              labelStyle={{
                fontFamily: GTFonts.semibold,
              }}
              onPress={() => onCancel('later')}
              style={{
                borderRadius: theme.borderRadius.md,
              }}>
              <Typography style={{ color: theme.colors.textSecondary }}>
                Later
              </Typography>
            </Button>
            <Button
              mode="contained"
              labelStyle={{
                fontFamily: GTFonts.semibold,
              }}
              style={{
                backgroundColor: theme.colors.success,
                borderRadius: theme.borderRadius.md,
                paddingHorizontal: s(5),
              }}
              onPress={onAccept}>
              {hideStars ? 'Give 5 Star Review' : `Give ${rating} Star`}
            </Button>
          </Dialog.Actions>
        </Animated.View>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: {
    // background and border handled via theme in-line
  },
  dialogConstrained: {
    maxWidth: WEB_MODAL_MAX_WIDTH,
    alignSelf: 'center',
    width: '90%',
  },
  title: {
    textAlign: 'center',
    fontFamily: GTFonts.semibold,
    fontSize: fs(20),
    lineHeight: fs(24),
  },
  message: {
    fontFamily: GTFonts.regular,
    fontSize: fs(16),
    lineHeight: fs(24),
  },
  starsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: v(8),
  },
});

export default RatingPromptHost;

function Star({
  index,
  filled,
  onPress,
}: {
  index: number;
  filled: boolean;
  onPress: () => void;
}) {
  const { theme } = useTheme();
  const scale = useSharedValue(filled ? 1.15 : 1);

  useEffect(() => {
    scale.value = withTiming(filled ? 1.15 : 1, { duration: 160 });
  }, [filled]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  return (
    <Animated.View style={animatedStyle} layout={Layout.springify()}>
      <IconButton
        icon={filled ? 'star' : 'star-outline'}
        size={iconSize(28)}
        iconColor={
          filled ? theme.colors.warningDark : theme.colors.textSecondary
        }
        onPress={onPress}
      />
    </Animated.View>
  );
}
